import React, { useEffect, useState, useMemo } from 'react';
import { LogoUNEA } from '../../components/common/Logo';
import { useFrontDeskAuth } from '../../context/FrontDeskAuthContext';
import FrontDeskLogin from '../frontdesk/FrontDeskLogin';
import { useAuth } from '../../context/AuthContext';
import MIcon from '../../components/common/MIcon';

const ACCENT   = '#004174';
const NAVY     = '#004174';
const LIGHT    = '#F7F9FC';
const BORDER   = '#D1DCE8';

const STATUS_LABEL = { pending:'Pendiente', confirmed:'Confirmada', rejected:'Rechazada', cancelled:'Cancelada' };
const STATUS_COLOR = { pending:'#B45309', confirmed:'#15803D', rejected:'#B91C1C', cancelled:'#6B7280' };
const STATUS_BG    = { pending:'#FFFBEB', confirmed:'#F0FDF4', rejected:'#FEF2F2', cancelled:'#F9FAFB' };

function pad(n) { return String(n).padStart(2,'0'); }
function fmtDate(d) { return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`; }
function fmtTime(d) { return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }
function fmtDisplay(iso) {
  const d = new Date(iso);
  return d.toLocaleString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'});
}

/* ── Mini calendario semanal ── */
function WeekCalendar({ bookings, selectedDate, onSelectDate }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - d.getDay() + 1);
    return d;
  });

  const days = Array.from({length:7}, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  const bookingsByDay = useMemo(() => {
    const map = {};
    bookings.filter(b => b.status !== 'rejected' && b.status !== 'cancelled').forEach(b => {
      const key = fmtDate(new Date(b.start_time));
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [bookings]);

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
        <button onClick={() => { const d=new Date(weekStart); d.setDate(d.getDate()-7); setWeekStart(d); }}
          style={{background:'none',border:'none',cursor:'pointer',color:'#6B7280',fontSize:18,lineHeight:1}}>‹</button>
        <span style={{fontSize:12,fontWeight:600,color:'#6B7280'}}>
          {weekStart.toLocaleDateString('es-ES',{month:'long',year:'numeric'})}
        </span>
        <button onClick={() => { const d=new Date(weekStart); d.setDate(d.getDate()+7); setWeekStart(d); }}
          style={{background:'none',border:'none',cursor:'pointer',color:'#6B7280',fontSize:18,lineHeight:1}}>›</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:4}}>
        {['L','M','X','J','V','S','D'].map(d => (
          <div key={d} style={{textAlign:'center',fontSize:10,fontWeight:700,color:'#9CA3AF',paddingBottom:4}}>{d}</div>
        ))}
        {days.map(d => {
          const key    = fmtDate(d);
          const active = key === selectedDate;
          const count  = bookingsByDay[key] || 0;
          const isPast = d < new Date(fmtDate(new Date()));
          return (
            <button key={key} onClick={() => onSelectDate(key)} disabled={isPast}
              style={{
                border:'none', borderRadius:6, padding:'6px 2px', cursor: isPast?'default':'pointer',
                background: active ? ACCENT : 'transparent',
                color: active ? '#fff' : isPast ? '#D1D5DB' : '#111827',
                fontSize:12, fontWeight: active?700:400, position:'relative', textAlign:'center',
              }}>
              {d.getDate()}
              {count > 0 && (
                <span style={{position:'absolute',top:2,right:2,width:5,height:5,borderRadius:'50%',
                  background: active ? 'rgba(255,255,255,.7)' : ACCENT}}/>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Franja de horario visual ── */
function TimeSlots({ bookings, selectedDate, onSelect }) {
  const hours = Array.from({length:13}, (_,i) => 8+i); // 8:00 - 20:00
  const dayBookings = bookings.filter(b => {
    if (b.status === 'rejected' || b.status === 'cancelled') return false;
    return fmtDate(new Date(b.start_time)) === selectedDate;
  });

  function isOccupied(h, m) {
    const t = new Date(`${selectedDate}T${pad(h)}:${pad(m)}:00`);
    return dayBookings.some(b => new Date(b.start_time) <= t && new Date(b.end_time) > t);
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:2,maxHeight:320,overflowY:'auto'}}>
      {hours.map(h => (
        <div key={h} style={{display:'grid',gridTemplateColumns:'36px repeat(4,1fr)',gap:2,alignItems:'center'}}>
          <span style={{fontSize:10,color:'#9CA3AF',textAlign:'right',paddingRight:6}}>{pad(h)}:00</span>
          {[0,15,30,45].map(m => {
            const occ = isOccupied(h,m);
            return (
              <button key={m} onClick={() => !occ && onSelect(`${pad(h)}:${pad(m)}`)}
                style={{
                  height:20, border:'none', borderRadius:3, cursor: occ?'default':'pointer',
                  background: occ ? '#FCA5A5' : '#D1FAE5',
                  opacity: occ ? 0.8 : 1,
                  fontSize:9, color: occ?'#B91C1C':'#15803D',
                }}>
                {occ ? '●' : ''}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ── Modal de reserva ── */
function Field({ label, children }) {
  return (
    <div>
      <label style={{fontSize:11,fontWeight:600,color:'#6B7280',textTransform:'uppercase',letterSpacing:.4,display:'block',marginBottom:4}}>{label}</label>
      {children}
    </div>
  );
}

function BookingModal({ room, fdUser, selectedDate, selectedTime, selectedEndTime, onClose, onCreated, allRooms, allBookings }) {
  const [form, setForm] = React.useState({
    title:'', description:'', extra_rooms: [],
    date_start: selectedDate || fmtDate(new Date()),
    date_end:   selectedDate || fmtDate(new Date()),
    start: selectedTime || '09:00',
    end:   selectedEndTime || (selectedTime ? `${pad(parseInt(selectedTime.split(':')[0])+1)}:${selectedTime.split(':')[1]}` : '10:00'),
    recurrent: false,
    repeat_every: 1,
    repeat_type: 'Días',
    repeat_days: [],
    repeat_until: selectedDate || fmtDate(new Date()),
    month_mode: 'day',
    month_day: null,
    month_week: '1',
    month_weekday: 'Lunes',
  });
  const [saving, setSaving] = React.useState(false);
  const [error,  setError]  = React.useState('');
  const f = v => setForm(p => ({...p, ...v}));

  // Detección de conflicto en tiempo real contra las reservas ya cargadas
  const conflictWarning = React.useMemo(() => {
    if (!allBookings?.length) return null;
    const s = new Date(`${form.date_start}T${form.start}:00`);
    const e = new Date(`${form.date_end}T${form.end}:00`);
    if (isNaN(s) || isNaN(e) || e <= s) return null;
    const hit = allBookings.find(b => {
      if (b.status === 'rejected' || b.status === 'cancelled') return false;
      if (b.room_id !== room.id) return false;
      const bs = new Date(b.start_time.replace(' ', 'T'));
      const be = new Date(b.end_time.replace(' ', 'T'));
      return s < be && e > bs;
    });
    return hit
      ? `Esta sala ya tiene reservada "${hit.title}" de ${hit.start_time.slice(11,16)} a ${hit.end_time.slice(11,16)}`
      : null;
  }, [form.date_start, form.date_end, form.start, form.end, allBookings, room.id]);

  const DAYS_ES = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];

  function toggleDay(d) {
    f({ repeat_days: form.repeat_days.includes(d) ? form.repeat_days.filter(x=>x!==d) : [...form.repeat_days, d] });
  }

  async function handleSubmit() {
    if (!form.title.trim()) return setError('El título es obligatorio');
    const start_time = `${form.date_start}T${form.start}:00`;
    const end_time   = `${form.date_end}T${form.end}:00`;
    if (new Date(end_time) <= new Date(start_time)) return setError('La fecha/hora de fin debe ser posterior al inicio');
    setSaving(true); setError('');
    try {
      const token = localStorage.getItem('fd_token') || localStorage.getItem('hd_token');
      const body = {
        title: form.title, description: form.description,
        start_time, end_time,
        requester_name:  fdUser?.name  || fdUser?.full_name || fdUser?.email || 'Anónimo',
        requester_email: fdUser?.email || '',
        recurrent: form.recurrent,
        ...(form.recurrent ? {
          repeat_every:   form.repeat_every,
          repeat_type:    form.repeat_type,
          repeat_days:    form.repeat_days,
          repeat_until:   form.repeat_until,
          month_mode:     form.month_mode,
          month_day:      form.month_day,
          month_week:     form.month_week,
          month_weekday:  form.month_weekday,
        } : {}),
      };
      const res = await fetch(`/api/rooms/${room.id}/bookings`, {
        method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al crear la reserva');
      onCreated(data);
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:14,width:'100%',maxWidth:560,boxShadow:'0 8px 40px rgba(0,0,0,.15)',overflow:'hidden'}}>
        <div style={{padding:'16px 20px',display:'flex',justifyContent:'space-between',alignItems:'center',borderBottom:'1px solid #F3F4F6'}}>
          <div style={{fontSize:16,fontWeight:700,color:'#111827'}}>Nueva reserva</div>
          <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#9CA3AF'}}>×</button>
        </div>
        <div style={{padding:'20px',display:'flex',flexDirection:'column',gap:14,maxHeight:'70vh',overflowY:'auto'}}>
          {/* Título */}
          <Field label="Título *">
            <input value={form.title} onChange={e=>f({title:e.target.value})} placeholder="Ej. Reunión de equipo…"
              style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
          </Field>
          {/* Fechas */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Field label="Fecha inicio">
              <input type="date" value={form.date_start} onChange={e=>f({date_start:e.target.value,date_end:e.target.value})}
                style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
            </Field>
            <Field label="Fecha fin">
              <input type="date" value={form.date_end} onChange={e=>f({date_end:e.target.value})}
                style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
            </Field>
          </div>
          {/* Horas */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Field label="Hora inicio">
              <input type="time" step="900" value={form.start} onChange={e=>f({start:e.target.value})}
                style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
            </Field>
            <Field label="Hora fin">
              <input type="time" step="900" value={form.end} onChange={e=>f({end:e.target.value})}
                style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
            </Field>
          </div>
          {/* Ubicación y espacios */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Field label="Ubicación">
              <div style={{padding:'6px 8px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,background:'#F9FAFB',color:'#374151',display:'flex',flexWrap:'wrap',gap:4,minHeight:38,alignItems:'center'}}>
                {[...new Set([room.location, ...form.extra_rooms.map(id=>(allRooms||[]).find(x=>x.id===id)?.location).filter(Boolean)])].map((loc,i)=>(
                  <span key={i} style={{background:'#F3F4F6',borderRadius:6,padding:'2px 8px',fontSize:12,color:'#374151'}}>{loc}</span>
                ))}
              </div>
            </Field>
            <Field label="Espacios">
              <div style={{border:'1px solid #E5E7EB',borderRadius:7,background:'#fff',position:'relative',minHeight:38}}>
                {/* Select fijo arriba a la derecha */}
                <select onChange={e=>{ if(e.target.value) f({extra_rooms:[...new Set([...form.extra_rooms,Number(e.target.value)])]}); e.target.value=''; }}
                  style={{position:'absolute',top:6,right:6,border:'none',outline:'none',fontSize:13,color:'#9CA3AF',cursor:'pointer',background:'transparent',zIndex:1}}>
                  <option value=""></option>
                  {(allRooms||[]).filter(r2=>r2.id!==room.id && !form.extra_rooms.includes(r2.id)).map(r2=>(
                    <option key={r2.id} value={r2.id}>{r2.name}</option>
                  ))}
                </select>
                {/* Chips con wrap, padding derecho para no solapar la flecha */}
                <div style={{padding:'4px 30px 4px 8px',display:'flex',flexWrap:'wrap',gap:4,alignItems:'center',minHeight:38}}>
                  <span style={{background:'#E5E7EB',color:'#374151',borderRadius:12,padding:'2px 10px',fontSize:12,fontWeight:600}}>{room.name}</span>
                  {form.extra_rooms.map(id => {
                    const r2 = (allRooms||[]).find(x=>x.id===id);
                    return r2 ? (
                      <span key={id} style={{background:'#E5E7EB',color:'#374151',borderRadius:12,padding:'2px 8px',fontSize:12,fontWeight:500,display:'flex',alignItems:'center',gap:3}}>
                        {r2.name}
                        <button type="button" onClick={()=>f({extra_rooms:form.extra_rooms.filter(x=>x!==id)})}
                          style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',fontSize:13,lineHeight:1,padding:0}}>×</button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            </Field>
          </div>
          {/* Descripción */}
          <Field label="Descripción completa (opcional)">
            <textarea value={form.description} onChange={e=>f({description:e.target.value})} rows={3} placeholder="Detalles adicionales…"
              style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none',resize:'none'}}/>
          </Field>
          {/* Periodicidad */}
          <div style={{borderTop:'1px solid #F3F4F6',paddingTop:12}}>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:600,color:'#374151'}}>
              <input type="checkbox" checked={form.recurrent} onChange={e=>f({recurrent:e.target.checked})}
                style={{width:16,height:16,cursor:'pointer',accentColor:ACCENT}}/>
              Se repite
            </label>
            {form.recurrent === true && (
              <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:10}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <Field label="Repetir cada">
                    <input type="number" min="1" value={form.repeat_every} onChange={e=>f({repeat_every:Number(e.target.value)})}
                      style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
                  </Field>
                  <Field label="Tipo repetición">
                    <select value={form.repeat_type} onChange={e=>f({repeat_type:e.target.value})}
                      style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,outline:'none',background:'#fff'}}>
                      {['Días','Semanas','Meses'].map(t=><option key={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                {form.repeat_type === 'Semanas' && (
                  <Field label="Se repite el">
                    <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
                      {DAYS_ES.map(d => (
                        <button key={d} type="button" onClick={()=>toggleDay(d)}
                          style={{padding:'4px 10px',borderRadius:16,border:'1px solid',fontSize:12,cursor:'pointer',
                            borderColor: form.repeat_days.includes(d)?ACCENT:'#E5E7EB',
                            background:  form.repeat_days.includes(d)?ACCENT:'#fff',
                            color:       form.repeat_days.includes(d)?'#fff':'#374151',fontWeight:500}}>
                          {d.slice(0,2)}
                        </button>
                      ))}
                    </div>
                  </Field>
                )}
                {form.repeat_type === 'Meses' && (
                  <Field label="Se repite el">
                    <div style={{display:'flex',flexDirection:'column',gap:8,marginTop:4}}>
                      {/* Opción A: día fijo del mes */}
                      <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer'}}>
                        <input type="radio" name="month_mode" value="day"
                          checked={form.month_mode !== 'week'}
                          onChange={()=>f({month_mode:'day'})}
                          style={{accentColor:ACCENT}}/>
                        <span>El día</span>
                        <input type="number" min="1" max="31"
                          value={form.month_day || new Date(form.date_start).getDate()}
                          onChange={e=>f({month_day:Number(e.target.value)})}
                          disabled={form.month_mode === 'week'}
                          style={{width:52,padding:'4px 8px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:13,
                            outline:'none',textAlign:'center',opacity:form.month_mode==='week'?.4:1}}/>
                        <span>de cada mes</span>
                      </label>
                      {/* Opción B: Nth semana del mes */}
                      <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer',flexWrap:'wrap'}}>
                        <input type="radio" name="month_mode" value="week"
                          checked={form.month_mode === 'week'}
                          onChange={()=>f({month_mode:'week'})}
                          style={{accentColor:ACCENT}}/>
                        <span>La</span>
                        <select value={form.month_week || '1'}
                          onChange={e=>f({month_week:e.target.value})}
                          disabled={form.month_mode !== 'week'}
                          style={{padding:'4px 8px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:13,
                            outline:'none',background:'#fff',opacity:form.month_mode!=='week'?.4:1}}>
                          <option value="1">primera</option>
                          <option value="2">segunda</option>
                          <option value="3">tercera</option>
                          <option value="4">cuarta</option>
                          <option value="-1">última</option>
                        </select>
                        <select value={form.month_weekday || DAYS_ES[0]}
                          onChange={e=>f({month_weekday:e.target.value})}
                          disabled={form.month_mode !== 'week'}
                          style={{padding:'4px 8px',border:'1px solid #E5E7EB',borderRadius:6,fontSize:13,
                            outline:'none',background:'#fff',opacity:form.month_mode!=='week'?.4:1}}>
                          {DAYS_ES.map(d=><option key={d} value={d}>{d}</option>)}
                        </select>
                        <span>de cada mes</span>
                      </label>
                    </div>
                  </Field>
                )}
                <Field label="Tope de repetición">
                  <input type="date" value={form.repeat_until} onChange={e=>f({repeat_until:e.target.value})}
                    style={{width:'100%',padding:'8px 10px',border:'1px solid #E5E7EB',borderRadius:7,fontSize:13,boxSizing:'border-box',outline:'none'}}/>
                </Field>
              </div>
            )}
          </div>
          {conflictWarning && (
            <div style={{padding:'10px 12px',background:'#FFFBEB',border:'1px solid #F59E0B',borderRadius:7,fontSize:12,color:'#92400E',display:'flex',alignItems:'flex-start',gap:8}}>
              <span style={{fontSize:15,flexShrink:0}}>⚠️</span>
              <span><strong>Choque de horario:</strong> {conflictWarning}. Ajusta la fecha u hora antes de confirmar.</span>
            </div>
          )}
          {!!room.requires_approval && (
            <div style={{padding:'10px 12px',background:'#FFFBEB',border:'1px solid #FCD34D',borderRadius:7,fontSize:12,color:'#92400E'}}>
              ⏳ Esta sala requiere aprobación. Recibirás un email cuando se gestione tu solicitud.
            </div>
          )}
          {error && <div style={{padding:'8px 12px',background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:7,fontSize:12,color:'#B91C1C'}}>{error}</div>}
        </div>
        <div style={{padding:'14px 20px',borderTop:'1px solid #F3F4F6',display:'flex',justifyContent:'flex-end',gap:8}}>
          <button onClick={onClose} style={{padding:'8px 16px',borderRadius:7,border:'1px solid #E5E7EB',background:'#fff',cursor:'pointer',fontSize:13}}>Cancelar</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{padding:'8px 20px',borderRadius:7,border:'none',background:ACCENT,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:600,opacity:saving?.7:1}}>
            {saving ? 'Enviando…' : room.requires_approval ? 'Solicitar reserva' : 'Confirmar reserva'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Tarjeta de sala ── */
function RoomCard({ room, onSelect }) {
  const eq = Array.isArray(room.equipment) ? room.equipment : [];
  return (
    <div onClick={() => onSelect(room)}
      style={{background:'#fff',borderRadius:12,border:'1px solid #E5E7EB',overflow:'hidden',cursor:'pointer',transition:'box-shadow .15s',boxShadow:'0 1px 3px rgba(0,0,0,.05)'}}
      onMouseOver={e=>e.currentTarget.style.boxShadow='0 4px 16px rgba(0,133,202,.12)'}
      onMouseOut={e=>e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,.05)'}>
      {/* Foto o placeholder */}
      <div style={{height:140,background:`linear-gradient(135deg,${room.category_color||ACCENT}22,${room.category_color||ACCENT}44)`,display:'flex',alignItems:'center',justifyContent:'center',position:'relative'}}>
        {room.photos?.length > 0
          ? <img src={room.photos[0]} alt={room.name} style={{width:'100%',height:'100%',objectFit:'cover',position:'absolute',inset:0}}/>
          : <MIcon name="meeting_room" size={48} style={{color:room.category_color||ACCENT,opacity:.4}}/>
        }
        {!!room.requires_approval && (
          <span style={{position:'absolute',top:8,right:8,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,background:'#FFFBEB',color:'#92400E',border:'1px solid #FCD34D'}}>
            Requiere aprobación
          </span>
        )}
        {room.category_name && (
          <span style={{position:'absolute',top:8,left:8,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,background:room.category_color||ACCENT,color:'#fff'}}>
            {room.category_name}
          </span>
        )}
      </div>
      <div style={{padding:'14px 16px'}}>
        <div style={{fontSize:14,fontWeight:700,color:'#111827',marginBottom:4}}>{room.name}</div>
        {room.location && <div style={{fontSize:12,color:'#6B7280',marginBottom:8}}><MIcon name="place" size={12}/> {room.location}</div>}
        <div style={{display:'flex',gap:12,fontSize:12,color:'#6B7280',marginBottom:8}}>
          <span><MIcon name="group" size={12}/> {room.capacity} personas</span>
        </div>
        {eq.length > 0 && (
          <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
            {eq.slice(0,4).map((e,i) => (
              <span key={i} style={{fontSize:10,padding:'2px 7px',borderRadius:10,background:LIGHT,color:NAVY,border:`1px solid ${BORDER}`}}>{e}</span>
            ))}
            {eq.length > 4 && <span style={{fontSize:10,color:'#9CA3AF'}}>+{eq.length-4}</span>}
          </div>
        )}
      </div>
    </div>
  );
}


/* ── Vista Gantt — eje X salas, eje Y horas ── */
const HOUR_START = 7;
const HOUR_END   = 22;
const SLOT_H     = 23; // px por franja de 30 min
const COL_W      = 160; // px por columna de sala
const LABEL_W    = 44;  // px columna de hora

function GanttView({ rooms, allBookings, selectedDate, onSelectSlot, onClickBooking }) {
  const slots = [];
  for (let h = HOUR_START; h < HOUR_END; h++) {
    slots.push({ h, m: 0 });
    slots.push({ h, m: 30 });
  }

  function slotTop(timeStr) {
    const [h, m] = timeStr.split(':').map(Number);
    return ((h - HOUR_START) * 2 + m / 30) * SLOT_H;
  }

  function slotHeight(startStr, endStr) {
    const [sh, sm] = startStr.split(':').map(Number);
    const [eh, em] = endStr.split(':').map(Number);
    const startSlots = (sh - HOUR_START) * 2 + sm / 30;
    const endSlots   = (eh - HOUR_START) * 2 + em / 30;
    return Math.max(1, endSlots - startSlots) * SLOT_H;
  }

  const totalH = slots.length * SLOT_H;

  // Agrupar reservas por sala para el día seleccionado
  const bookingsByRoom = {};
  (allBookings || []).forEach(b => {
    if (b.status === 'rejected' || b.status === 'cancelled') return;
    const d = b.start_time?.slice(0, 10);
    if (d !== selectedDate) return;
    if (!bookingsByRoom[b.room_id]) bookingsByRoom[b.room_id] = [];
    bookingsByRoom[b.room_id].push(b);
  });

  // ── Drag to select ───────────────────────────────────────
  const dragState = React.useRef(null); // { room, startSlot }
  const [selection, setSelection] = React.useState(null); // { roomId, startSlot, endSlot }

  function slotIndexFromY(y, containerTop) {
    return Math.floor((y - containerTop) / SLOT_H);
  }

  function slotIndexToTime(idx) {
    const totalSlots = idx;
    const h = HOUR_START + Math.floor(totalSlots / 2);
    const m = (totalSlots % 2) * 30;
    return `${pad(Math.min(h, HOUR_END))}:${pad(m)}`;
  }

  function handleMouseDown(e, room, containerRef) {
    if (e.button !== 0) return;
    e.preventDefault();
    const rect = containerRef.getBoundingClientRect();
    const startIdx = Math.floor((e.clientY - rect.top) / SLOT_H);
    dragState.current = { room, startIdx, containerRef };
    setSelection({ roomId: room.id, startSlot: startIdx, endSlot: startIdx + 1 });
  }

  function handleMouseMove(e) {
    if (!dragState.current) return;
    const rect = dragState.current.containerRef.getBoundingClientRect();
    const currentIdx = Math.ceil((e.clientY - rect.top) / SLOT_H);
    setSelection(s => s ? { ...s, endSlot: Math.max(s.startSlot + 1, currentIdx) } : null);
  }

  function handleMouseUp() {
    if (!dragState.current || !selection) { dragState.current = null; setSelection(null); return; }
    const { room } = dragState.current;
    const startTime = slotIndexToTime(selection.startSlot);
    const endTime   = slotIndexToTime(selection.endSlot);
    dragState.current = null;
    setSelection(null);
    onSelectSlot(room, startTime, endTime);
  }

  React.useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [selection]);

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', minWidth: LABEL_W + rooms.length * 72, overflow: 'hidden', boxSizing: 'border-box' }}>

      {/* ── FILA DE CABECERAS (separada del cuerpo para que todas tengan la misma altura) ── */}
      <div style={{ display: 'flex', width: '100%', borderBottom: '1px solid #E5E7EB', background: '#F7F9FC', alignItems: 'stretch' }}>
        {/* Celda vacía de la columna de horas */}
        <div style={{ width: LABEL_W, flexShrink: 0, borderRight: '1px solid #F3F4F6' }} />
        {/* Cabeceras de sala */}
        {rooms.map(room => (
          <div key={room.id} style={{
            flex: 1, minWidth: 72, borderRight: '1px solid #F3F4F6',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
            padding: '6px 8px', textAlign: 'center', gap: 2,
          }}>
            <div style={{ minHeight: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              {room.category_name && (
                <div style={{ fontSize: 9, fontWeight: 700, color: room.category_color || ACCENT, background: (room.category_color || ACCENT) + '18', borderRadius: 8, padding: '1px 5px', letterSpacing: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                  {room.category_name}
                </div>
              )}
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', lineHeight: 1.2, textAlign: 'center', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {room.name}
            </div>
            <div style={{ fontSize: 9, color: '#9CA3AF' }}>
              {room.capacity}p
            </div>
          </div>
        ))}
      </div>

      {/* ── CUERPO: columna de horas + columnas de slots (siempre alineadas) ── */}
      <div style={{ display: 'flex', width: '100%' }}>

        {/* Columna de horas */}
        <div style={{ width: LABEL_W, flexShrink: 0, borderRight: '1px solid #F3F4F6' }}>
          {slots.map(({ h, m }) => (
            <div key={`${h}:${m}`} style={{
              height: SLOT_H, borderBottom: '1px solid #F3F4F6',
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              paddingRight: 8, fontSize: 11, color: '#9CA3AF', fontWeight: m === 0 ? 600 : 400,
              background: m === 0 ? '#F7F9FC' : '#fff',
            }}>
              {`${pad(h)}:${pad(m)}`}
            </div>
          ))}
        </div>

        {/* Columnas de slots por sala */}
        {rooms.map(room => {
          const roomBookings = bookingsByRoom[room.id] || [];
          const containerRef = React.createRef();
          const isSelecting  = selection?.roomId === room.id;
          const selTop    = isSelecting ? selection.startSlot * SLOT_H : 0;
          const selHeight = isSelecting ? (selection.endSlot - selection.startSlot) * SLOT_H : 0;
          return (
            <div key={room.id}
              ref={containerRef}
              style={{ flex: 1, minWidth: 72, borderRight: '1px solid #F3F4F6', position: 'relative', height: totalH, userSelect: 'none' }}
              onMouseDown={e => handleMouseDown(e, room, containerRef.current || e.currentTarget)}>

              {/* Franjas de fondo */}
              {slots.map(({ h, m }) => (
                <div key={`${h}:${m}`} style={{
                  height: SLOT_H, borderBottom: `1px solid ${m === 0 ? '#E5E7EB' : '#F3F4F6'}`,
                  cursor: 'crosshair', background: m === 0 ? '#F7F9FC' : '#fff',
                }} />
              ))}

              {/* Selección visual */}
              {isSelecting && selHeight > 0 && (
                <div style={{
                  position: 'absolute', left: 2, right: 2, top: selTop, height: selHeight,
                  background: 'rgba(0,133,202,.2)', border: '2px solid #0085CA',
                  borderRadius: 4, pointerEvents: 'none', zIndex: 3,
                }} />
              )}

              {/* Bloques de reservas */}
              {roomBookings.map(b => {
                const startT = b.start_time?.slice(11, 16) || '00:00';
                const endT   = b.end_time?.slice(11, 16)   || '00:00';
                const top    = slotTop(startT);
                const height = slotHeight(startT, endT);
                if (top < 0 || top > totalH) return null;
                return (
                  <div key={b.id}
                    onMouseDown={e => { e.stopPropagation(); e.preventDefault(); }}
                    onClick={e => { e.stopPropagation(); onClickBooking(b, room, { x: e.clientX, y: e.clientY }); }}
                    style={{
                      position: 'absolute', left: 3, right: 3, top, height: height - 2,
                      background: b.status === 'pending' ? '#FEF3C7' : '#679ac3',
                      border: b.status === 'pending' ? '1px solid #F59E0B' : 'none',
                      borderRadius: 5, padding: '3px 6px', cursor: 'pointer',
                      overflow: 'hidden', zIndex: 2,
                      boxShadow: 'none',
                      transition: 'filter .1s',
                    }}
                    onMouseOver={e => e.currentTarget.style.filter = 'brightness(0.92)'}
                    onMouseOut={e => e.currentTarget.style.filter = 'none'}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: b.status === 'pending' ? '#92400E' : '#fff', lineHeight: 1.3, overflow: 'hidden' }}>
                      {b.title}
                    </div>
                    {height > 40 && (
                      <div style={{ fontSize: 10, color: b.status === 'pending' ? '#B45309' : 'rgba(255,255,255,.8)', marginTop: 2 }}>
                        {startT} – {endT}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Selector de fecha ── */
function DateNav({ selectedDate, onChange }) {
  const d    = new Date(selectedDate + 'T12:00:00');
  const prev = new Date(d); prev.setDate(d.getDate() - 1);
  const next = new Date(d); next.setDate(d.getDate() + 1);
  const isToday = selectedDate === fmtDate(new Date());

  const DAYS = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
  const MONTHS = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button onClick={() => onChange(fmtDate(prev))}
        style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 16, color: '#6B7280', lineHeight: 1 }}>‹</button>

      {/* Fecha clickable con showPicker */}
      {(() => {
        const dateRef = React.useRef(null);
        return (
          <div style={{ textAlign: 'center', minWidth: 200, cursor: 'pointer' }}
            onClick={() => { try { dateRef.current?.showPicker(); } catch { dateRef.current?.click(); } }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', textTransform: 'capitalize' }}>
              {DAYS[d.getDay()]}, {d.getDate()} de {MONTHS[d.getMonth()]}
            </div>
                <input ref={dateRef} type="date" value={selectedDate}
              onChange={e => e.target.value && onChange(e.target.value)}
              style={{ position: 'absolute', opacity: 0, width: 1, height: 1, pointerEvents: 'none' }}/>
          </div>
        );
      })()}

      <button onClick={() => onChange(fmtDate(next))}
        style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 16, color: '#6B7280', lineHeight: 1 }}>›</button>
      {!isToday && (
        <button onClick={() => onChange(fmtDate(new Date()))}
          style={{ background: LIGHT, border: `1px solid ${BORDER}`, borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 12, color: NAVY, fontWeight: 600 }}>
          Hoy
        </button>
      )}
    </div>
  );
}

/* ── Popup info reserva — estilo Google Calendar ── */
function BookingPopup({ booking, room, onClose, onBook, pos }) {
  const start = booking.start_time?.slice(11,16) || '';
  const end   = booking.end_time?.slice(11,16)   || '';
  const date  = booking.start_time ? new Date(booking.start_time.replace(' ','T')) : null;
  const DAYS  = ['domingo','lunes','martes','miércoles','jueves','viernes','sábado'];
  const MONTHS= ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  const dateStr = date ? `${DAYS[date.getDay()]}, ${date.getDate()} de ${MONTHS[date.getMonth()]}` : '';
  const isPending = booking.status === 'pending';

  return (
    <div style={{ position:'fixed', inset:0, zIndex:500 }} onClick={onClose}>
      {/* Card posicionado cerca del click */}
      {(() => {
        const W = 380, H = 420;
        const vw = window.innerWidth, vh = window.innerHeight;
        let left = pos ? pos.x + 12 : vw/2 - W/2;
        let top  = pos ? pos.y - 20 : vh/2 - H/2;
        if (left + W > vw - 12) left = (pos?.x || vw/2) - W - 12;
        if (top  + H > vh - 12) top  = vh - H - 12;
        if (top  < 12)          top  = 12;
        if (left < 12)          left = 12;
        return (
      <div style={{
        position:'absolute', top, left,
        background:'#fff', borderRadius:8, width:W, maxWidth:'95vw',
        boxShadow:'0 8px 40px rgba(0,0,0,.28)', overflow:'hidden',
        animation:'fadeIn .12s ease',
      }} onClick={e => e.stopPropagation()}>

        {/* Header con color de estado */}
        <div style={{ background: isPending ? '#FEF3C7' : ACCENT, padding:'16px 16px 12px', position:'relative' }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <div style={{ width:14, height:14, borderRadius:3, background: isPending ? '#F59E0B' : '#fff', opacity:.9, flexShrink:0, marginTop:2 }}/>
              <div>
                <div style={{ fontSize:18, fontWeight:700, color: isPending ? '#92400E' : '#fff', lineHeight:1.3 }}>
                  {booking.title}
                </div>
                <div style={{ fontSize:12, color: isPending ? '#B45309' : 'rgba(255,255,255,.8)', marginTop:2 }}>
                  {isPending ? 'Pendiente de aprobación' : 'Confirmada'}
                </div>
              </div>
            </div>
            <button onClick={onClose}
              style={{ background:'none', border:'none', cursor:'pointer', color: isPending ? '#92400E' : 'rgba(255,255,255,.8)', fontSize:20, lineHeight:1, padding:4, flexShrink:0 }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:12 }}>
          {/* Fecha y hora */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
            <MIcon name="schedule" size={18} style={{ color:'#6B7280', marginTop:1, flexShrink:0 }}/>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'#111827', textTransform:'capitalize' }}>{dateStr}</div>
              <div style={{ fontSize:13, color:'#6B7280' }}>{start} – {end}</div>
            </div>
          </div>
          {/* Sala */}
          <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
            <MIcon name="meeting_room" size={18} style={{ color:'#6B7280', marginTop:1, flexShrink:0 }}/>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{room?.name}</div>
              {room?.location && <div style={{ fontSize:12, color:'#9CA3AF' }}>{room.location}</div>}
              {room?.capacity && <div style={{ fontSize:12, color:'#9CA3AF' }}>{room.capacity} personas</div>}
            </div>
          </div>
          {/* Solicitante */}
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <MIcon name="person" size={18} style={{ color:'#6B7280', flexShrink:0 }}/>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:'#111827' }}>{booking.requester_name}</div>
              {booking.requester_email && <div style={{ fontSize:12, color:'#9CA3AF' }}>{booking.requester_email}</div>}
            </div>
          </div>
          {/* Asistentes */}
          {booking.attendees > 1 && (
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <MIcon name="group" size={18} style={{ color:'#6B7280', flexShrink:0 }}/>
              <div style={{ fontSize:13, color:'#374151' }}>{booking.attendees} asistentes</div>
            </div>
          )}
          {/* Descripción */}
          {booking.description && (
            <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
              <MIcon name="notes" size={18} style={{ color:'#6B7280', marginTop:1, flexShrink:0 }}/>
              <div style={{ fontSize:13, color:'#374151', lineHeight:1.5 }}>{booking.description}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px 16px', borderTop:'1px solid #F3F4F6', display:'flex', gap:8 }}>
          <button onClick={onClose}
            style={{ flex:1, padding:'8px', borderRadius:7, border:'1px solid #E5E7EB', background:'#fff', cursor:'pointer', fontSize:13, color:'#374151' }}>
            Cerrar
          </button>
          <button onClick={() => { onClose(); onBook(room); }}
            style={{ flex:1, padding:'8px', borderRadius:7, border:'none', background:ACCENT, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:600 }}>
            Reservar esta sala
          </button>
        </div>
      </div>
        );
      })()}
    </div>
  );
}

/* ── Página principal ── */
export default function RoomsPage() {
  const { canAccess, fdLoading, fdUser, fdLogout } = useFrontDeskAuth();
  const { agent } = useAuth();
  const effectiveAccess = canAccess || !!agent;

  const [rooms,        setRooms]        = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [locations,    setLocations]    = useState([]);
  const [allBookings,  setAllBookings]  = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedDate, setSelectedDate] = useState(fmtDate(new Date()));
  const [selectedTime,    setSelectedTime]    = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [showBooking,  setShowBooking]  = useState(false);
  const [lastBooking,  setLastBooking]  = useState(null);
  const [filterLoc,    setFilterLoc]    = useState(null);
  const [filterCat,    setFilterCat]    = useState(null);
  function handleLocChange(v) { setFilterLoc(v); setFilterCat(null); }
  const [searchQuery,  setSearchQuery]  = useState('');
  const [popupBooking, setPopupBooking] = useState(null);
  const [popupPos,     setPopupPos]     = useState(null);
  const [popupRoom,    setPopupRoom]    = useState(null);
  const [view,         setView]         = useState('gantt');
  const [bookingsV,    setBookingsV]    = useState(0);

  useEffect(() => {
    document.title = 'Reserva de Espacios — UNEATLANTICO';
    return () => { document.title = 'HelpDesk TI — UNEATLANTICO'; };
  }, []);

  useEffect(() => {
    if (!effectiveAccess) return;
    Promise.all([
      fetch('/api/rooms').then(r => r.json()),
      fetch('/api/rooms/categories').then(r => r.json()),
      fetch('/api/rooms/locations').then(r => r.json()),
    ]).then(([r, cats, locs]) => { setRooms(r); setCategories(cats); setLocations(locs); if (locs.length > 0) setFilterLoc(locs[0].id); })
      .finally(() => setLoading(false));
  }, [effectiveAccess]);

  useEffect(() => {
    if (!effectiveAccess || rooms.length === 0) return;
    const from = selectedDate + 'T00:00:00';
    const to   = selectedDate + 'T23:59:59';
    Promise.all(
      rooms.map(r => fetch(`/api/rooms/${r.id}/bookings?from=${from}&to=${to}`)
        .then(res => res.json()).catch(() => []))
    ).then(results => setAllBookings(results.flat()));
  }, [effectiveAccess, selectedDate, rooms, bookingsV]);

  const filtered = useMemo(() => rooms
    .filter(r => filterLoc === null || Number(r.location_id) === Number(filterLoc))
    .filter(r => filterCat === null || Number(r.category_id) === Number(filterCat))
    .sort((a, b) => {
      const ca = a.category_name || '';
      const cb = b.category_name || '';
      if (ca !== cb) return ca.localeCompare(cb);
      return (a.name || '').localeCompare(b.name || '');
    }), [rooms, filterLoc, filterCat]);

  const availableCats = useMemo(() => {
    const seen = new Set();
    return rooms
      .filter(r => filterLoc === null || Number(r.location_id) === Number(filterLoc))
      .filter(r => r.category_id && r.category_name)
      .filter(r => { if (seen.has(r.category_id)) return false; seen.add(r.category_id); return true; })
      .map(r => ({ id: r.category_id, name: r.category_name }))
      .sort((a,b) => a.name.localeCompare(b.name));
  }, [rooms, filterLoc]);

  const ganttRooms = filtered;

  if (fdLoading && !agent) return null;
  if (!effectiveAccess) return <FrontDeskLogin error={null}/>;

  return (
    <div className="rooms-page" style={{minHeight:'100vh',background:'#F5F6F8',fontFamily:"'Inter','Arial',sans-serif"}}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:scale(1)}} html,body{overflow-x:hidden!important;max-width:100vw!important;} .rooms-page{width:100vw;max-width:100vw;overflow-x:hidden;}`}</style>
      {/* AppBar */}
      <header style={{height:56,background:'#fff',borderBottom:'2px solid #0085CA',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 24px',position:'sticky',top:0,zIndex:100,width:'100%',boxSizing:'border-box'}}>
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          <LogoUNEA height={32}/>
          <div style={{width:1,height:28,background:'#E5E7EB',margin:'0 16px'}}/>
          <span style={{fontSize:15,fontWeight:500,color:'#111827'}}>Reserva de Espacios</span>
        </div>
        {fdUser && (
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {fdUser.picture
              ? <img src={fdUser.picture} alt="" style={{width:28,height:28,borderRadius:'50%',border:`2px solid ${BORDER}`}}/>
              : <div style={{width:28,height:28,borderRadius:'50%',background:ACCENT,color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,fontWeight:700}}>{fdUser.name?.[0]?.toUpperCase()}</div>
            }
            <span style={{fontSize:13,color:'#6B7280'}}>{fdUser.name}</span>
            <button onClick={fdLogout} style={{fontSize:12,color:'#9CA3AF',background:'none',border:'none',cursor:'pointer'}}>Salir</button>
          </div>
        )}
      </header>

      <div style={{padding:'20px 32px',boxSizing:'border-box',width:'100%',overflow:'hidden'}}>
        {/* Toolbar */}
        {/* Toolbar */}
        <div style={{display:'flex',gap:10,marginBottom:16,alignItems:'center',justifyContent:'space-between',flexWrap:'wrap'}}>
          {view === 'gantt' && <DateNav selectedDate={selectedDate} onChange={setSelectedDate}/>}
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {view === 'grid' && (
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar sala…"
              style={{padding:'5px 10px',borderRadius:7,border:'1px solid #E5E7EB',fontSize:12,outline:'none',width:160}}/>
          )}
          {/* Select Ubicación */}
          <div style={{position:'relative'}}>
            <label style={{position:'absolute',top:-8,left:8,fontSize:10,fontWeight:600,color:'#9CA3AF',background:'#F5F6F8',padding:'0 4px',letterSpacing:.3,textTransform:'uppercase'}}>Ubicación</label>
            <select value={filterLoc ?? ''} onChange={e => handleLocChange(e.target.value ? Number(e.target.value) : null)}
              style={{padding:'6px 28px 6px 10px',borderRadius:7,border:'1px solid #E5E7EB',fontSize:12,outline:'none',background:'#fff',cursor:'pointer',appearance:'none',WebkitAppearance:'none',minWidth:160}}>
              <option value="">Todas</option>
              {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
            </select>
            <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:'#9CA3AF',fontSize:10}}>&#9662;</span>
          </div>
          {/* Select Categoría */}
          <div style={{position:'relative'}}>
            <label style={{position:'absolute',top:-8,left:8,fontSize:10,fontWeight:600,color:'#9CA3AF',background:'#F5F6F8',padding:'0 4px',letterSpacing:.3,textTransform:'uppercase'}}>Categoría</label>
            <select value={filterCat ?? ''} onChange={e => setFilterCat(e.target.value ? Number(e.target.value) : null)}
              style={{padding:'6px 28px 6px 10px',borderRadius:7,border:'1px solid #E5E7EB',fontSize:12,outline:'none',background:'#fff',cursor:'pointer',appearance:'none',WebkitAppearance:'none',minWidth:140}}>
              <option value="">Todas</option>
              {availableCats.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
            <span style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:'#9CA3AF',fontSize:10}}>&#9662;</span>
          </div>
          {/* Switcher vista */}
          {[['gantt','calendar_view_week','Gantt'],['grid','grid_view','Salas']].map(([v,icon,label]) => (
            <button key={v} onClick={()=>setView(v)}
              style={{display:'flex',alignItems:'center',gap:5,padding:'6px 14px',borderRadius:7,border:'1px solid'+(view===v?ACCENT:'#E5E7EB'),background:view===v?LIGHT:'#fff',color:view===v?ACCENT:'#6B7280',fontSize:12,fontWeight:600,cursor:'pointer'}}>
              <MIcon name={icon} size={14}/>{label}
            </button>
          ))}
          </div>
        </div>

        {loading ? (
          <div style={{textAlign:'center',padding:'60px 0',color:'#9CA3AF'}}>Cargando espacios…</div>
        ) : view === 'gantt' ? (
          <div>

            {ganttRooms.length === 0
              ? <div style={{textAlign:'center',padding:'60px 0',color:'#9CA3AF',fontSize:14}}>No hay espacios disponibles</div>
              : <div style={{width:'100%',overflowX:'auto'}}><GanttView
                  rooms={ganttRooms}
                  allBookings={allBookings}
                  selectedDate={selectedDate}
                  onSelectSlot={(room, startTime, endTime) => { setSelectedRoom(room); setSelectedTime(startTime); setSelectedEndTime(endTime); setShowBooking(true); }}
                  onClickBooking={(b, room, pos) => { setPopupBooking(b); setPopupRoom(room); setPopupPos(pos); }}
                />
            </div>}
          </div>
        ) : (() => {
          const q = searchQuery.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'');
          const searchFiltered = filtered.filter(r =>
            (r.name||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').includes(q) ||
            (r.category_name||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').includes(q) ||
            (r.location_name||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').includes(q)
          );
          // Agrupar por ubicación
          const groups = [];
          const seen = new Map();
          searchFiltered.forEach(r => {
            const key = r.location_id ?? '__none__';
            if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ name: r.location_name || 'Sin ubicación', color: r.location_color, rooms: [] }); }
            groups[seen.get(key)].rooms.push(r);
          });
          return (
            <div style={{display:'flex',flexDirection:'column',gap:28}}>
              {groups.map(g => (
                <div key={g.name}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                    {g.color && <span style={{width:10,height:10,borderRadius:'50%',background:g.color,display:'inline-block'}}/>}
                    <span style={{fontSize:13,fontWeight:700,color:'#374151'}}>{g.name}</span>
                    <span style={{fontSize:12,color:'#9CA3AF'}}>({g.rooms.length} {g.rooms.length===1?'sala':'salas'})</span>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
                    {g.rooms.map(r => <RoomCard key={r.id} room={r} onSelect={r => { setSelectedRoom(r); setView('gantt'); }}/>)}
                  </div>
                </div>
              ))}
              {groups.length === 0 && <div style={{textAlign:'center',padding:'60px 0',color:'#9CA3AF',fontSize:14}}>No se encontraron espacios</div>}
            </div>
          );
        })()}
      </div>

      {popupBooking && (
        <BookingPopup
          booking={popupBooking} room={popupRoom} pos={popupPos}
          onClose={() => { setPopupBooking(null); setPopupRoom(null); setPopupPos(null); }}
          onBook={room => { setSelectedRoom(room); setSelectedTime(null); setShowBooking(true); setPopupBooking(null); }}
        />
      )}

      {showBooking && selectedRoom && (
        <BookingModal
          room={selectedRoom} fdUser={fdUser || agent} allRooms={rooms} allBookings={allBookings}
          selectedDate={selectedDate} selectedTime={selectedTime} selectedEndTime={selectedEndTime}
          onClose={() => { setShowBooking(false); setSelectedRoom(null); }}
          onCreated={b => { setLastBooking(b); setShowBooking(false); setSelectedRoom(null); setBookingsV(v => v + 1); }}
        />
      )}
    </div>
  );
}